/**
 * Validador mínimo de JSON Schema (subset do draft 2020-12) para os props dos
 * cartões do chat — spec-001 do `project-plataforma-por-chat`, [T004] / US-2.
 *
 * ## Por que existe um validador aqui, e não uma lib
 *
 * O `package.json` não tem `ajv`, e trazer um validador completo (~120 KB no
 * bundle) para conferir um punhado de keywords num payload de cartão é caro
 * pelo que entrega. Os schemas que circulam aqui são cópias byte-idênticas dos
 * `contratos/` do backend, e o que eles usam está em `KEYWORDS` (validação)
 * e `KEYWORDS_DE_ANOTACAO` (metadados que não validam nada).
 *
 * O corolário importante: **isto não é um validador de JSON Schema**. Keyword
 * fora das duas listas é IGNORADA em runtime — e é por isso que
 * `validar.unit.js` varre `schemas/*.json` e fica vermelho quando um contrato
 * passa a usar uma keyword que não está em nenhuma delas. Um schema do
 * backend que dependesse de uma keyword ignorada ficaria aprovado aqui sem
 * nada ficar vermelho; o caminho é implementar a keyword (com caso positivo e
 * negativo no `.unit.js`), não confiar que "passou".
 *
 * ## Por que ele reprova em vez de deixar renderizar
 *
 * O `ui.props` de um cartão vem de um turno de tool, escolhido pelo MODELO.
 * Montar um componente com props fora do contrato produz erro de render dentro
 * da bolha da conversa — o gestor lê um pedaço de stack trace no meio da
 * resposta. Reprovar antes vira `fallback-card` com o motivo `schema`, que é
 * legível e diz o que fazer.
 */

// As keywords que este validador entende. Também servem para reconhecer um
// schema "de verdade" ao desembrulhar o módulo do `import()` (ver abaixo).
export const KEYWORDS = [
  'type',
  'required',
  'properties',
  'enum',
  'const',
  'items',
  'additionalProperties',
  'anyOf',
  'oneOf',
  'allOf',
  '$ref',
  'minimum',
  'maximum',
  // ★ `exclusiveMinimum` entrou em 20/09/2026 com `ui-cliente-segmentacao-carteira`
  // (spec-005): a régua de custódia e o ROA da premissa são `> 0`, e é essa a
  // diferença que importa — `minimum: 0` aceitaria uma régua de R$ 0,00, que
  // classificaria a carteira inteira como "fica" e pareceria um dado válido na
  // tela. Implementada de verdade em `limitesDeNumero`, nunca declarada como
  // anotação: keyword que reprova listada como metadado seria um contrato que
  // o front diz conferir e não confere.
  'exclusiveMinimum',
  'minLength',
  'maxLength',
  'minItems',
  'maxItems',
  'pattern',
]

// Keywords que os contratos carregam mas que NÃO validam nada: metadados do
// draft e `format`, que em 2020-12 é anotação por default (nenhum contrato
// nosso depende dele para reprovar). `$defs` é o alvo do `$ref`, não uma regra.
//
// ★ `examples` entrou em 19/09/2026 com `ui-documento-panorama` e
// `ui-documento-preparo-de-reuniao`: é ANOTAÇÃO pelo próprio draft 2020-12
// (§9.5 — "this keyword ... MUST NOT affect validation"), então ignorá-la é
// o comportamento correto, não uma lacuna. ⚠️ O caminho ERRADO seria tirar a
// keyword do schema do backend para o validador parar de reclamar: a cópia
// aqui é byte a byte, e mutilar o contrato do outro lado para caber neste
// validador inverte quem manda no contrato.
export const KEYWORDS_DE_ANOTACAO = [
  '$schema',
  '$id',
  '$defs',
  '$comment',
  'title',
  'description',
  'format',
  // `examples` (spec-013 [T007]) — igual a `title`/`description`: documenta o
  // valor esperado do campo (`ui-documento-panorama.schema.json#/properties/
  // titulo/examples`) sem participar da validação. Draft 2020-12 trata como
  // anotação por default, o mesmo motivo de `format` acima.
  'examples',
]

const PREFIXO_REF_LOCAL = '#/$defs/'

// Um predicado por tipo do JSON Schema.
//
// `number` recusa `NaN` de propósito: `typeof NaN === 'number'` é verdade e
// seria o único jeito de um "número" chegar ao cartão e sair na tela como
// `NaN` — que é justamente o que o contrato existe para impedir.
const TIPOS = {
  string: (v) => typeof v === 'string',
  number: (v) => typeof v === 'number' && !Number.isNaN(v),
  integer: (v) => typeof v === 'number' && Number.isInteger(v),
  boolean: (v) => typeof v === 'boolean',
  object: (v) => !!v && typeof v === 'object' && !Array.isArray(v),
  array: (v) => Array.isArray(v),
  null: (v) => v === null,
}

const temProprio = (objeto, chave) =>
  Object.prototype.hasOwnProperty.call(objeto, chave)

/**
 * O schema, já fora do módulo do `import()`.
 *
 * `entrada.schema` é `() => import('...schema.json')`, e o que volta é o
 * namespace do módulo (`{ default: {...} }`), não o schema. Desembrulhar só
 * quando NENHUMA keyword está no topo evita o falso positivo com o schema que
 * legitimamente declara a keyword `default`.
 */
const desembrulhar = (schema) => {
  if (!schema || typeof schema !== 'object') return schema
  if (KEYWORDS.some((k) => temProprio(schema, k))) return schema
  const conteudo = schema.default
  if (conteudo && typeof conteudo === 'object') return conteudo
  return schema
}

const tipoBate = (tipo, dado) => {
  const lista = Array.isArray(tipo) ? tipo : [tipo]
  return lista.some((nome) => {
    const predicado = TIPOS[nome]
    // Tipo que não conhecemos não reprova: ignorar é a política deste subset.
    return predicado ? predicado(dado) : true
  })
}

const nomeDoTipo = (tipo) => (Array.isArray(tipo) ? tipo.join('|') : tipo)

// `const` nos contratos é sempre primitivo (`true`, nome de operação); o
// `JSON.stringify` cobre o caso raro de objeto sem trazer um deep-equal.
const mesmoValor = (a, b) => a === b || JSON.stringify(a) === JSON.stringify(b)

const filho = (ctx, caminho) => ({ ...ctx, caminho })

/**
 * A parte de objeto: `required`, `additionalProperties` e a descida em
 * `properties`. Extraída de `coletar` para manter as duas abaixo do teto de
 * complexidade do ESLint — a divisão é por forma do dado, não por keyword.
 */
function coletarDeObjeto(s, dado, ctx) {
  const propriedades = s.properties || {}

  // `undefined` é ausente; `null` PRESENTE satisfaz o required — é o que o
  // backend manda quando o campo existe e não tem valor (`resultado: null`).
  if (Array.isArray(s.required)) {
    s.required.forEach((campo) => {
      if (dado[campo] === undefined) {
        ctx.erros.push(ctx.caminho + '.' + campo + ': required')
      }
    })
  }

  if (s.additionalProperties === false) {
    Object.keys(dado).forEach((campo) => {
      if (!temProprio(propriedades, campo)) {
        ctx.erros.push(ctx.caminho + '.' + campo + ': not allowed')
      }
    })
  }

  Object.keys(propriedades).forEach((campo) => {
    if (dado[campo] === undefined) return
    coletar(propriedades[campo], dado[campo], filho(ctx, ctx.caminho + '.' + campo))
  })
}

/**
 * Limites de faixa/tamanho, um por forma do dado. Só medem quando o tipo
 * bate — o tipo errado já reprovou antes e encerrou o ramo.
 */
function limitesDeNumero(s, dado, caminho, erros) {
  if (s.minimum !== undefined && dado < s.minimum) erros.push(caminho + ': expected >= ' + s.minimum)
  if (s.maximum !== undefined && dado > s.maximum) erros.push(caminho + ': expected <= ' + s.maximum)
  if (s.exclusiveMinimum !== undefined && dado <= s.exclusiveMinimum) {
    erros.push(caminho + ': expected > ' + s.exclusiveMinimum)
  }
}

// `pattern` inválido no contrato é erro DO CONTRATO: vira `{ok: false}` como
// os outros, em vez de `SyntaxError` estourando na bolha.
function regexDoPattern(pattern) {
  try {
    return new RegExp(pattern)
  } catch (erro) {
    return null
  }
}

function conferirPattern(s, dado, caminho, erros) {
  if (typeof s.pattern !== 'string') return
  const regex = regexDoPattern(s.pattern)
  if (!regex) erros.push(caminho + ': invalid pattern ' + s.pattern)
  else if (!regex.test(dado)) {
    erros.push(caminho + ': expected pattern ' + s.pattern)
  }
}

function limitesDeString(s, dado, caminho, erros) {
  // Comprimento em pontos de código, como o draft manda (não em UTF-16).
  const tamanho = Array.from(dado).length
  if (s.minLength !== undefined && tamanho < s.minLength) erros.push(caminho + ': expected minLength ' + s.minLength)
  if (s.maxLength !== undefined && tamanho > s.maxLength) erros.push(caminho + ': expected maxLength ' + s.maxLength)
  conferirPattern(s, dado, caminho, erros)
}

function limitesDeArray(s, dado, caminho, erros) {
  if (s.minItems !== undefined && dado.length < s.minItems) erros.push(caminho + ': expected minItems ' + s.minItems)
  if (s.maxItems !== undefined && dado.length > s.maxItems) erros.push(caminho + ': expected maxItems ' + s.maxItems)
}

function coletarDeLimites(s, dado, ctx) {
  if (typeof dado === 'number') limitesDeNumero(s, dado, ctx.caminho, ctx.erros)
  if (typeof dado === 'string') limitesDeString(s, dado, ctx.caminho, ctx.erros)
  if (Array.isArray(dado)) limitesDeArray(s, dado, ctx.caminho, ctx.erros)
}

/** Os erros de UM ramo, sem tocar o acumulador de quem chamou. */
const errosDoRamo = (ramo, dado, ctx) => {
  const erros = []
  coletar(ramo, dado, { ...ctx, erros })
  return erros
}

const maisProximoDe = (listas) =>
  listas.reduce((melhor, atual) => (!melhor || atual.length < melhor.length ? atual : melhor), null)

/**
 * `anyOf`: aprova quando ao menos UM ramo aprova. Sem ramo aprovado, entram os
 * erros do ramo MAIS PRÓXIMO (o de menos erros) — quem conserta o contrato
 * precisa ler o estado que quase bateu, não a soma dos erros de todos os ramos.
 */
function coletarDeAnyOf(ramos, dado, ctx) {
  const resultados = ramos.map((ramo) => errosDoRamo(ramo, dado, ctx))
  if (resultados.some((erros) => !erros.length)) return
  const maisProximo = maisProximoDe(resultados)
  if (maisProximo) maisProximo.forEach((erro) => ctx.erros.push(erro))
}

/**
 * `oneOf`: aprova quando EXATAMENTE um ramo aprova. É o que impõe a
 * exclusividade `resultado`/`resultado_indisponivel` do cartão de simulação —
 * os dois juntos seriam lidos como "a economia é zero" ao lado de um motivo.
 */
function coletarDeOneOf(ramos, dado, ctx) {
  const resultados = ramos.map((ramo) => errosDoRamo(ramo, dado, ctx))
  const aprovados = resultados.filter((erros) => !erros.length).length
  if (aprovados === 1) return
  if (aprovados > 1) {
    ctx.erros.push(ctx.caminho + ': expected exactly one of ' + ramos.length + ' alternatives, ' + aprovados + ' matched')
    return
  }
  const maisProximo = maisProximoDe(resultados)
  if (maisProximo) maisProximo.forEach((erro) => ctx.erros.push(erro))
}

/**
 * `$ref` só local e só para `#/$defs/<nome>` — é a única forma que os
 * contratos usam. Referência que não resolve é erro de contrato, não silêncio.
 */
function coletarDeRef(ref, dado, ctx) {
  const nome = typeof ref === 'string' && ref.indexOf(PREFIXO_REF_LOCAL) === 0 ? ref.slice(PREFIXO_REF_LOCAL.length) : ''
  const defs = (ctx.raiz && ctx.raiz.$defs) || {}
  if (!nome || !temProprio(defs, nome)) {
    ctx.erros.push(ctx.caminho + ': unresolved $ref ' + ref)
    return
  }
  coletar(defs[nome], dado, ctx)
}

// `enum`/`const` encerram o ramo como o tipo errado: descer num valor que
// não é o esperado só produz erro em cascata.
function erroDeValor(s, dado) {
  if (Array.isArray(s.enum) && s.enum.indexOf(dado) === -1) return ': expected one of ' + s.enum.join(', ')
  if (temProprio(s, 'const') && !mesmoValor(s.const, dado)) return ': expected const ' + JSON.stringify(s.const)
  return ''
}

function coletarDeItens(s, dado, ctx) {
  if (!s.items) return
  dado.forEach((item, indice) => {
    coletar(s.items, item, filho(ctx, ctx.caminho + '[' + indice + ']'))
  })
}

function coletarDeCombinadores(s, dado, ctx) {
  if (typeof s.$ref === 'string') coletarDeRef(s.$ref, dado, ctx)
  if (Array.isArray(s.allOf)) s.allOf.forEach((ramo) => coletar(ramo, dado, ctx))
  if (Array.isArray(s.anyOf)) coletarDeAnyOf(s.anyOf, dado, ctx)
  if (Array.isArray(s.oneOf)) coletarDeOneOf(s.oneOf, dado, ctx)
}

/**
 * Acumula os erros de `dado` contra `schema` em `ctx.erros`, prefixando cada
 * um com `ctx.caminho`. `ctx.raiz` é o schema de topo — alvo dos `$ref`.
 *
 * Acumula em vez de parar no primeiro porque quem lê o resultado é um humano
 * consertando o contrato: ver os três campos errados de uma vez é uma rodada
 * de correção, não três.
 */
function coletar(schema, dado, ctx) {
  const s = desembrulhar(schema)
  if (!s || typeof s !== 'object') return
  const { caminho, erros } = ctx

  if (s.type !== undefined && !tipoBate(s.type, dado)) {
    erros.push(caminho + ': expected ' + nomeDoTipo(s.type))
    // Tipo errado encerra este ramo: descer em `properties`/`items` de um dado
    // que não é do tipo esperado só produz erro em cascata, sem informação nova.
    return
  }

  const erroValor = erroDeValor(s, dado)
  if (erroValor) {
    erros.push(caminho + erroValor)
    return
  }

  coletarDeLimites(s, dado, ctx)
  if (TIPOS.object(dado)) coletarDeObjeto(s, dado, ctx)
  if (Array.isArray(dado)) coletarDeItens(s, dado, ctx)
  coletarDeCombinadores(s, dado, ctx)
}

/**
 * Confere `dado` contra `schema`.
 *
 * @param {Object|null} schema  Schema (ou o módulo do `import()` dele). Ausente
 *                              devolve `ok: true` — sem contrato não há o que
 *                              violar.
 * @param {*} dado              O valor a conferir (tipicamente `ui.props`).
 * @param {string} caminho      Raiz das mensagens. Default `'props'`, que é o
 *                              que o cartão recebe.
 * @returns {{ ok: boolean, erros: string[] }} Erros no formato
 *          `'props.inputs: required'` / `'props.n: expected number'`.
 */
export function validar(schema, dado, caminho) {
  const erros = []
  coletar(schema, dado, { caminho: caminho || 'props', erros, raiz: desembrulhar(schema) })
  return { ok: erros.length === 0, erros }
}

export default validar
